import { supabase } from "@/shared/lib/supabase";
import { stripCountySuffix } from "@/shared/utils/addressUtils";

import type { AuthorityVerificationDocumentType } from "@/shared/constants/authorityVerification";
import type { PersonContact } from "@/shared/types/estateContact";

type Fields = Record<string, string | undefined>;

const NAME_SUFFIXES = new Set(["jr", "sr", "ii", "iii", "iv"]);

function nameTokens(name: string | null | undefined): string[] {
  return (name ?? "")
    .toLowerCase()
    .replace(/[^a-z\s'-]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

const nameParts = (name: string | null | undefined) =>
  nameTokens(name).filter((part) => !NAME_SUFFIXES.has(part));

const nameSuffix = (name: string | null | undefined) =>
  nameTokens(name).find((part) => NAME_SUFFIXES.has(part)) ?? null;

const contactName = (contact: PersonContact) =>
  `${contact.first_name ?? ""} ${contact.last_name ?? ""}`;

/**
 * Matches on first and last name, ignoring case and middle names. A suffix (Jr, Sr)
 * breaks a tie, and a name that stays ambiguous matches no one.
 */
export function findContactIdByName(
  contacts: PersonContact[] | undefined,
  name: string | undefined,
): number | null {
  const parts = nameParts(name);
  if (parts.length < 2) return null;
  const [first, last] = [parts[0], parts[parts.length - 1]];
  const matches = (contacts ?? []).filter((contact) => {
    const contactParts = nameParts(contactName(contact));
    return contactParts[0] === first && contactParts[contactParts.length - 1] === last;
  });
  if (matches.length <= 1) return matches[0]?.id ?? null;
  const suffix = nameSuffix(name);
  const sameSuffix = matches.filter((contact) => nameSuffix(contactName(contact)) === suffix);
  return sameSuffix.length === 1 ? sameSuffix[0].id : null;
}

const isoDate = (value: string | undefined): string | null =>
  value && /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? value.trim() : null;

const text = (value: string | undefined): string | null => value?.trim() || null;

/** Drops empty values, so a confirmation never erases what extraction already filled. */
function present<T extends Record<string, unknown>>(values: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== null && value !== undefined),
  ) as Partial<T>;
}

export async function saveConfirmedWill(
  estateId: number,
  fields: Fields,
  contacts: PersonContact[] | undefined,
  contactIds: Record<string, number | null> = {},
): Promise<{ executorId: number | null; successorId: number | null }> {
  const executorId =
    contactIds.namedExecutor ?? findContactIdByName(contacts, fields.namedExecutor);
  const successorId =
    contactIds.successorExecutor ?? findContactIdByName(contacts, fields.successorExecutor);
  const { error } = await supabase.from("estate_wills").upsert(
    {
      estate_id: estateId,
      has_will: "yes",
      ...present({
        date_signed: isoDate(fields.dateSigned),
        executor_contact_id: executorId,
        successor_executor_contact_id: successorId,
      }),
    },
    { onConflict: "estate_id" },
  );
  if (error) throw error;
  return { executorId, successorId };
}

const FIDUCIARY_TITLES = new Set(["executor", "administrator", "trustee"]);

const CASE_TYPES_BY_TITLE: Record<string, string> = {
  executor: "probate",
  administrator: "administration",
};

type LettersDocumentType = Exclude<AuthorityVerificationDocumentType, "trust">;

const CASE_TYPES_BY_DOCUMENT: Record<LettersDocumentType, string> = {
  letters_testamentary: "probate",
  letters_administration: "administration",
};

async function findCourtId(courtAddress: string | undefined): Promise<number | null> {
  const [county, state] = (courtAddress ?? "").split(",").map((part) => part.trim());
  if (!county || !state) return null;
  const { data } = await supabase
    .from("courts")
    .select("id")
    .eq("state", state.toUpperCase())
    .eq("county", stripCountySuffix(county).toUpperCase())
    .order("id")
    .limit(1)
    .maybeSingle();
  return data?.id ?? null;
}

export async function saveConfirmedLetters(
  estateId: number,
  fields: Fields,
  userContactId: number | null,
  documentType: LettersDocumentType,
): Promise<void> {
  const title = text(fields.fiduciaryTitle)?.toLowerCase() ?? null;
  const issued = isoDate(fields.dateIssued);
  const filed = isoDate(fields.dateFiled);
  const values = present({
    case_number: text(fields.caseNumber),
    filing_date: filed,
    issuance_date: issued,
    status: issued ? "letters_granted" : filed ? "filed" : null,
    fiduciary_title: title && FIDUCIARY_TITLES.has(title) ? title : null,
    fiduciary_contact_id: userContactId,
    court_id: await findCourtId(fields.courtAddress),
  });

  const { data: cases, error: casesError } = await supabase
    .from("cases")
    .select("id, case_type")
    .eq("estate_id", estateId)
    .order("id");
  if (casesError) throw casesError;

  const caseType = CASE_TYPES_BY_TITLE[title ?? ""] ?? CASE_TYPES_BY_DOCUMENT[documentType];
  const primary = (cases ?? []).find((c) => c.case_type !== "ancillary");
  const { error } = primary
    ? await supabase
        .from("cases")
        .update({ ...values, case_type: caseType })
        .eq("id", primary.id)
    : await supabase.from("cases").insert({ ...values, estate_id: estateId, case_type: caseType });
  if (error) throw error;
}

function trustType(label: string | undefined): string | null {
  const value = label?.toLowerCase() ?? "";
  if (value.startsWith("revocable")) return "revocable";
  if (value.startsWith("irrevocable")) return "irrevocable";
  return null;
}

export async function saveConfirmedTrust(
  estateId: number,
  fields: Fields,
  userContactId: number | null,
  contacts: PersonContact[] | undefined,
  contactIds: Record<string, number | null> = {},
): Promise<void> {
  const { error: flagError } = await supabase
    .from("estates")
    .update({ has_trust: "yes" })
    .eq("estate_id", estateId);
  if (flagError) throw flagError;

  const name = text(fields.trustName);
  const isTrustee = text(fields.legalRole)?.toLowerCase().includes("trustee") ?? false;
  const values = present({
    name,
    trust_type: trustType(fields.trustType),
    date_created: isoDate(fields.dateSigned),
    acting_trustee_contact_id:
      contactIds.actingTrustee ??
      findContactIdByName(contacts, fields.actingTrustee) ??
      (isTrustee ? userContactId : null),
    successor_trustee_contact_id:
      contactIds.successorTrustee ?? findContactIdByName(contacts, fields.successorTrustee),
  });

  const { data: trusts, error: trustsError } = await supabase
    .from("estate_trusts")
    .select("id, name")
    .eq("estate_id", estateId)
    .order("id");
  if (trustsError) throw trustsError;

  const existing =
    (trusts ?? []).find((t) => name && t.name?.toLowerCase() === name.toLowerCase()) ??
    (trusts?.length === 1 && !trusts[0].name ? trusts[0] : undefined);
  const { error } = existing
    ? await supabase.from("estate_trusts").update(values).eq("id", existing.id)
    : await supabase.from("estate_trusts").insert({ ...values, estate_id: estateId });
  if (error) throw error;
}
